import { z } from 'zod'

/**
 * Resumable device/human handoff contract — Part Three C5, register §6.
 *
 * An opaque operation ID binds the intended action, the account (where applicable),
 * the originating host grant, the relevant revision and the expiry. URLs carry the
 * opaque operation ID and nothing else: never document text, payment credentials or
 * decryption keys. Returning selected content to the host requires separate explicit
 * authorization, and completion is reported only after the local or server commit.
 */

const Id = z.string().min(1).max(128)
const IsoDateTime = z.string().datetime()

/** The seven C5 states. */
export const HandoffState = z.enum([
  'WAITING_FOR_USER',
  'WAITING_FOR_DEVICE',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'EXPIRED',
])
export type HandoffState = z.infer<typeof HandoffState>

/** Allowed state-machine transitions. Terminal states never reopen (C5). */
export const HANDOFF_TRANSITIONS: Readonly<Record<HandoffState, readonly HandoffState[]>> = {
  WAITING_FOR_USER: ['WAITING_FOR_DEVICE', 'RUNNING', 'FAILED', 'CANCELLED', 'EXPIRED'],
  WAITING_FOR_DEVICE: ['WAITING_FOR_USER', 'RUNNING', 'FAILED', 'CANCELLED', 'EXPIRED'],
  RUNNING: ['SUCCEEDED', 'FAILED', 'CANCELLED', 'EXPIRED'],
  SUCCEEDED: [],
  FAILED: [],
  CANCELLED: [],
  EXPIRED: [],
}

export const HANDOFF_TERMINAL_STATES: readonly HandoffState[] = [
  'SUCCEEDED',
  'FAILED',
  'CANCELLED',
  'EXPIRED',
]

/** A revoked host grant cancels an open handoff; it never resumes silently (C5). */
export function canTransitionHandoff(from: HandoffState, to: HandoffState): boolean {
  return HANDOFF_TRANSITIONS[from].includes(to)
}

/**
 * The only data a handoff URL may carry. `strict()` makes every other key — document
 * text, payment credentials, decryption keys, capability tokens — representable only
 * as a schema violation.
 */
export const HandoffUrlPayload = z
  .object({
    operation_id: z.string().min(1).max(255),
    /** Optional opaque non-secret selector for which device/pairing to resume. */
    pairing_ref: z.string().min(1).max(255).optional(),
  })
  .strict()
export type HandoffUrlPayload = z.infer<typeof HandoffUrlPayload>

/** Query parameters a handoff URL must never carry (C5, SEC-03). */
export const FORBIDDEN_HANDOFF_URL_PARAMS: readonly string[] = [
  'document_text',
  'document_bytes',
  'payment_credential',
  'card_number',
  'decryption_key',
  'key_material',
  'capability_token',
  'otp_code',
]

/** Forbidden parameter names present in a handoff URL query, sorted for stable output. */
export function findForbiddenHandoffUrlParams(
  parameterNames: readonly string[],
): readonly string[] {
  const forbidden = new Set(FORBIDDEN_HANDOFF_URL_PARAMS)
  return [...new Set(parameterNames.filter((name) => forbidden.has(name)))].sort()
}

export const HandoffIntendedAction = z
  .object({
    capability_id: z.string().min(1).max(128),
    action_ref: z.string().min(1).max(255),
  })
  .strict()
export type HandoffIntendedAction = z.infer<typeof HandoffIntendedAction>

/**
 * Handoff behaviour policy — C5. Cancellation, duplicate-open, expired-session and
 * host-revocation rules are declared as accepted values so no surface invents its own.
 */
export const HandoffPolicy = z
  .object({
    policy_version: z.number().int().positive(),
    url_carries_operation_id_only: z.literal(true),
    device_inventory_revealed_to_mcp: z.literal(false),
    content_return_requires_separate_authorization: z.literal(true),
    completion_reported_only_after_commit: z.literal(true),
    duplicate_open: z.literal('IDEMPOTENT_SINGLE_OPERATION'),
    wrong_device: z.literal('EXPLAIN_TRUTH'),
    expired_session: z.literal('EXPLAIN_TRUTH_WITH_RESTART'),
    host_revocation: z.literal('CANCEL_AND_REPORT'),
  })
  .strict()
export type HandoffPolicy = z.infer<typeof HandoffPolicy>

/* --------------------------- SM3: the handoff tools ------------------------ */

/**
 * How the person's document reaches SignBento. Three paths, and **no fourth**:
 *
 *  * `HOST_NATIVE_REFERENCE` — the host already holds a file handle the person selected in the
 *    host's own picker. The declaration `user_action: 'EXPLICIT_FILE_SELECTION'` is required, and
 *    the reference itself is never sent to SignBento: the bytes still travel through the secure
 *    upload slot, so a host that claims a native reference without the person having acted gets a
 *    continuation it cannot use rather than a silent import.
 *  * `SECURE_UPLOAD` — the fallback: a one-time upload slot. This is the path that makes
 *    base64-in-a-tool-argument unnecessary, which is why no such field exists anywhere.
 *  * `LOCAL_DEEP_LINK` — the app is opened on the person's own device and *they* choose the local
 *    item. Nothing about the device's inventory is ever listed to MCP.
 */
export const HANDOFF_SOURCE_MODES = [
  'HOST_NATIVE_REFERENCE',
  'SECURE_UPLOAD',
  'LOCAL_DEEP_LINK',
] as const
export const HandoffSourceMode = z.enum(HANDOFF_SOURCE_MODES)
export type HandoffSourceMode = z.infer<typeof HandoffSourceMode>

/**
 * The only thing a host may say about a file it holds: that the person picked it in the host's own
 * surface. A path, a name, a size and a digest are all absent, because a host-asserted file
 * identity is not evidence and must not become one.
 */
export const HostNativeReference = z
  .object({ user_action: z.literal('EXPLICIT_FILE_SELECTION') })
  .strict()
export type HostNativeReference = z.infer<typeof HostNativeReference>

/** Why a handoff did not succeed. Every value is a machine code the surface can explain honestly. */
export const HANDOFF_FAILURE_CODES = [
  /** The handoff was opened on a device that is not the one holding the source (C5). */
  'WRONG_DEVICE',
  /** The source is no longer available locally. Reported as such — never as a success (C5). */
  'SOURCE_UNAVAILABLE',
  /** Completion was reported without a local or server commit behind it (C5). */
  'NO_COMMIT',
  'USER_CANCELLED',
  'UPLOAD_EXPIRED',
  'UPLOAD_REJECTED',
  /** The originating host grant was revoked while the handoff was open (C5). */
  'GRANT_REVOKED',
] as const
export const HandoffFailureCode = z.enum(HANDOFF_FAILURE_CODES)
export type HandoffFailureCode = z.infer<typeof HandoffFailureCode>

/**
 * The explanations a surface may render. Message keys only: the C5 contract requires the truth to
 * be *said*, and this is the vocabulary in which it is said — no handoff failure ever renders as a
 * fabricated success, and none of them is prose a surface could soften.
 */
export const HANDOFF_EXPLANATION_KEYS: Readonly<Record<HandoffFailureCode, string>> = {
  WRONG_DEVICE: 'handoff.explanation.wrong_device',
  SOURCE_UNAVAILABLE: 'handoff.explanation.source_unavailable',
  NO_COMMIT: 'handoff.explanation.no_commit',
  USER_CANCELLED: 'handoff.explanation.user_cancelled',
  UPLOAD_EXPIRED: 'handoff.explanation.upload_expired',
  UPLOAD_REJECTED: 'handoff.explanation.upload_rejected',
  GRANT_REVOKED: 'handoff.explanation.grant_revoked',
}

/**
 * Why a running import is waiting: the person's workspace is full (DEV-026). The operation stays
 * running so they can remove items in SignBento and tap again; the Free key lets the host say that
 * Lifetime keeps more, without offering a purchase.
 */
export const HANDOFF_WORKSPACE_FULL_KEYS = {
  FREE: 'handoff.explanation.workspace_full_free',
  LIFETIME: 'handoff.explanation.workspace_full_lifetime',
} as const

/**
 * How long an operation stays open before it expires, and how long its upload slot lives. Thirty
 * minutes (Hamza, 27 Oct): long enough for the person to open the link, sign in and confirm; the
 * staged bytes are still purged when it ends.
 */
export const HANDOFF_TTL_SECONDS = 30 * 60
export const HANDOFF_UPLOAD_SLOT_SECONDS = 5 * 60

/**
 * The upload slot. The URL is a continuation, not a secret in a query string: the slot token
 * travels in the caller's authorization header (or body), never as a query parameter, and the URL
 * itself carries no document text, no credential and no key.
 */
export const HandoffUploadSlot = z
  .object({
    url: z.string().min(1).max(2048),
    method: z.literal('PUT'),
    /** The opaque, single-use slot credential. Never placed in a URL. */
    slot_token: z.string().min(1).max(512),
    expires_at: IsoDateTime,
    max_bytes: z.number().int().positive(),
    accepted_content_types: z.array(z.string().min(1).max(128)).min(1),
  })
  .strict()
export type HandoffUploadSlot = z.infer<typeof HandoffUploadSlot>

/**
 * What `signbento_begin_document_handoff` accepts. It is `strict()` and has no field for bytes, a
 * path, a base64 blob or document text: the "never base64/paste" rule is structural here, because
 * there is nowhere to put them (MCP-AC-031).
 */
export const HandoffBeginRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    capability_id: z.string().min(1).max(128),
    action_ref: z.string().min(1).max(255),
    source_mode: HandoffSourceMode,
    /** Present only for `HOST_NATIVE_REFERENCE`; the person's action in the host's own picker. */
    host_native_reference: HostNativeReference.optional(),
    /** The prepared review this handoff belongs to, when it belongs to one. */
    review_id: z.string().min(1).max(128).optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (
      request.source_mode === 'HOST_NATIVE_REFERENCE' &&
      request.host_native_reference === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'a host-native reference declares the explicit user action it came from',
        path: ['host_native_reference'],
      })
    }
    if (
      request.source_mode !== 'HOST_NATIVE_REFERENCE' &&
      request.host_native_reference !== undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'only a host-native reference carries a host file declaration',
        path: ['host_native_reference'],
      })
    }
  })
export type HandoffBeginRequest = z.infer<typeof HandoffBeginRequest>

/**
 * The begin result. Four literals carry the C5 behaviour rules so a host cannot claim any of them
 * was implied: nothing is activated, no device inventory is revealed, no background execution is
 * promised, and nothing has been returned to the host.
 */
export const HandoffBeginResult = z
  .object({
    operation_id: z.string().min(1).max(255),
    state: HandoffState,
    intended_action: HandoffIntendedAction,
    account_id: Id,
    host_grant_ref: z.string().min(1).max(255),
    revision: z.number().int().positive(),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
    /** Carries the opaque operation id and nothing else (C5). */
    url: z.string().min(1).max(2048),
    url_payload: HandoffUrlPayload,
    source_mode: HandoffSourceMode,
    upload_slot: HandoffUploadSlot.nullable(),
    /** No activation happens on a handoff: the operation never activates an envelope (MCP-AC-032). */
    activation_performed: z.literal(false),
    /** The device inventory is never listed to MCP; a deep link asks the device, not a list (C5). */
    device_inventory_revealed_to_mcp: z.literal(false),
    /** Never promise background execution while the app is suspended or closed (C5). */
    background_execution_promised: z.literal(false),
    /** Selected content reaches the host only after a separate explicit authorization (C5). */
    content_returned_to_host: z.literal(false),
    completion_reported_after_commit: z.literal(true),
    policy: HandoffPolicy,
  })
  .strict()
export type HandoffBeginResult = z.infer<typeof HandoffBeginResult>

/** What a poller sees. `explanation_key` is present exactly when the state needs explaining. */
export const HandoffStatusResult = z
  .object({
    operation_id: z.string().min(1).max(255),
    state: HandoffState,
    intended_action: HandoffIntendedAction,
    revision: z.number().int().positive(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
    expires_at: IsoDateTime,
    failure_code: HandoffFailureCode.nullable(),
    explanation_key: z.string().min(1).max(160).nullable(),
    /** A restart is offered when an expired session can be resumed with a new operation (C5). */
    restart_available: z.boolean(),
    /** Whether a separate authorization to return content to the host has been recorded. */
    content_return_authorized: z.boolean(),
    /** The minimum authorized outcome, when content was authorized: references and digests only. */
    returned_content: z
      .object({
        item_id: Id,
        revision: z.number().int().positive(),
        content_digest: z.string().min(1).max(128),
        bytes: z.number().int().nonnegative(),
      })
      .strict()
      .nullable(),
    activation_performed: z.literal(false),
    device_inventory_revealed_to_mcp: z.literal(false),
    background_execution_promised: z.literal(false),
  })
  .strict()
export type HandoffStatusResult = z.infer<typeof HandoffStatusResult>

/** A device resuming an open handoff. The device reference is the resuming device's own id. */
export const HandoffResumeRequest = z
  .object({
    device_ref: z.string().min(1).max(255).optional(),
  })
  .strict()
export type HandoffResumeRequest = z.infer<typeof HandoffResumeRequest>

/**
 * The resume answer. `wrong_device` is a *result*, not a failure: the operation stays open and the
 * answer says plainly that the source is on another device, instead of fabricating progress (C5).
 */
export const HandoffResumeResult = z
  .object({
    operation_id: z.string().min(1).max(255),
    state: HandoffState,
    wrong_device: z.boolean(),
    explanation_key: z.string().min(1).max(160).nullable(),
    /** The context preserved for this operation, so the device opens the right screen. */
    intended_action: HandoffIntendedAction,
    review_id: z.string().min(1).max(128).nullable(),
    activation_performed: z.literal(false),
  })
  .strict()
export type HandoffResumeResult = z.infer<typeof HandoffResumeResult>

/**
 * The completion report. `commit_ref` is the local or server commit the completion stands on; a
 * completion without one is refused as `NO_COMMIT` rather than recorded as a success (C5).
 */
export const HandoffCompleteRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    outcome: z.enum(['SUCCEEDED', 'FAILED']),
    /** Required for `SUCCEEDED`: the commit the completion is reported after. */
    commit_ref: z.string().min(1).max(255).optional(),
    /** The item the person produced, when the local work created one. */
    item_id: z.string().min(1).max(128).optional(),
    revision: z.number().int().positive().optional(),
    failure_code: HandoffFailureCode.optional(),
  })
  .strict()
  .superRefine((request, ctx) => {
    if (request.outcome === 'SUCCEEDED' && request.commit_ref === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'completion is reported only after the local or server commit',
        path: ['commit_ref'],
      })
    }
    if (request.outcome === 'FAILED' && request.failure_code === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a failed handoff explains why instead of fabricating success',
        path: ['failure_code'],
      })
    }
    if (request.outcome === 'SUCCEEDED' && request.failure_code !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'failure_code is only set on a failed handoff',
        path: ['failure_code'],
      })
    }
  })
export type HandoffCompleteRequest = z.infer<typeof HandoffCompleteRequest>

/** The separate, explicit authorization to return selected content to the host (C5). */
export const HandoffReturnAuthorizationRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    authorize_return_to_host: z.literal(true),
    item_id: Id,
    revision: z.number().int().positive(),
  })
  .strict()
export type HandoffReturnAuthorizationRequest = z.infer<typeof HandoffReturnAuthorizationRequest>

/**
 * The person's one tap on an uploaded document: "use this document". The app has rendered the staged
 * copy and counted its pages; the service checks the bytes it holds, makes them a published
 * workspace copy that the requesting connection can read, and completes the operation with that
 * copy's reference, which is what the host's `prepare` names as its source. The tap is the explicit
 * publication and the explicit return of the reference (C5); nothing else in the app does either.
 */
export const HandoffAcceptRequest = z
  .object({
    client_request_id: z.string().min(1).max(128),
    title: z.string().trim().min(1).max(300),
    pages: z.number().int().positive(),
  })
  .strict()
export type HandoffAcceptRequest = z.infer<typeof HandoffAcceptRequest>

/** An open handoff, as the app lists it so the person can find it without the link. */
export const HandoffPendingItem = z
  .object({
    operation_id: z.string().min(1).max(255),
    state: HandoffState,
    /** The connected assistant's own name, as it registered; null if it is no longer known. */
    host_name: z.string().min(1).max(300).nullable(),
    /** Whether a document has arrived and is waiting for the person's confirmation. */
    document_waiting: z.boolean(),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
export type HandoffPendingItem = z.infer<typeof HandoffPendingItem>

export const HandoffPendingList = z
  .object({ handoffs: z.array(HandoffPendingItem).max(20) })
  .strict()
export type HandoffPendingList = z.infer<typeof HandoffPendingList>

export const HandoffCompleteResult = HandoffStatusResult
export type HandoffCompleteResult = z.infer<typeof HandoffCompleteResult>

/**
 * The local-file deep link. It carries the opaque operation id and nothing else — no path, no
 * document text, no key — and it opens the app so the *person* chooses the local item.
 */
export function handoffDeepLink(operationId: string): string {
  const url = new URL('signbento://handoff')
  url.searchParams.set('operation_id', operationId)
  return url.toString()
}

export const HandoffOperation = z
  .object({
    operation_id: z.string().min(1).max(255),
    intended_action: HandoffIntendedAction,
    account_id: Id.optional(),
    host_grant_ref: z.string().min(1).max(255),
    /** Revision of the bound object the handoff was prepared against. */
    revision: z.number().int().positive(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime.optional(),
    expires_at: IsoDateTime,
    state: HandoffState,
    url_payload: HandoffUrlPayload,
    device_ref: z.string().min(1).max(255).optional(),
    failure_code: z.string().min(1).max(64).optional(),
  })
  .strict()
  .superRefine((operation, ctx) => {
    if (operation.state === 'FAILED' && operation.failure_code === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a failed handoff explains why instead of fabricating success',
        path: ['failure_code'],
      })
    }
    if (operation.state !== 'FAILED' && operation.failure_code !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'failure_code is only set on a failed handoff',
        path: ['failure_code'],
      })
    }
    if (operation.url_payload.operation_id !== operation.operation_id) {
      ctx.addIssue({
        code: 'custom',
        message: 'the URL carries this operation id and nothing else',
        path: ['url_payload', 'operation_id'],
      })
    }
  })
export type HandoffOperation = z.infer<typeof HandoffOperation>
